import classNames from 'classnames';
import React, { ReactNode, useEffect, useState } from 'react';
import { getRowPackageLayout } from './components/func';
import { RenderRowSelection } from './components/row-selection';
import {
  getColumnAttrs,
  getLevelSpacingStyle,
  isExpandDetail,
  isColumnCollapse,
  isRowTreeRender,
  isTreeExpand,
} from './libs';
import {
  getBodyCheckedStatus,
  // getStickyClassName
} from './libs/basic';
import {
  ITableRowSelection,
  ITableColumn,
  IString,
  ITableSummary,
  ITableHeader,
  ITableTree,
  TableTreeExpand,
  ITableExpand,
  CollapseColumnRenderType,
  TableExpandKeys,
} from './model';
import { SummaryComponent } from './components/summary';
import {
  removeIndexCache,
  addEmptyBody,
  TreeOperatePackage,
} from './components';
import { TableCellRoot } from './components/table-cell-root';
import { ExpandOperatePackage } from './components/expand/operate-package';
import { INumber } from '../../model';
import { IListItem } from '../auto-table/model';
import { CustomPopover } from '../custom-popover';

interface IProps<T> {
  readonly columns: ReadonlyArray<ITableColumn<T>>;
  readonly data: ReadonlyArray<T>;
  readonly fullData: ReadonlyArray<T>;
  readonly rowSelection?: ITableRowSelection<T>;
  readonly rowClassName?: string | ((record: T, index: number) => string);
  readonly summary?: ITableSummary<T>;
  readonly onRow?: (
    record: T,
    rowIndex: number,
    data: ReadonlyArray<T>
  ) => ITableHeader;
  readonly tree?: ITableTree;
  readonly rowKey?: string;
  readonly expandTree?: TableTreeExpand;
  readonly onChangeTreeExpand: (id: string, status: boolean) => void;
  readonly columnWidths: ReadonlyArray<number>;
  readonly isEnableResize: boolean;
  readonly isEnableRowDrag: boolean;
  readonly expandDetail?: ITableExpand<T>;
  readonly expandedKeys?: TableExpandKeys;
  readonly onChangeExpand?: (id: string, status: boolean) => void;
  readonly columnCollapseIndex: INumber<ReadonlyArray<number>>;
  readonly renderIconDown?: (isExpand: boolean) => React.ReactNode;
  readonly onDragStart?: (ev: any, rowIndex: number) => void;
  readonly onDragEnd?: (ev: any) => void;
  readonly isDragging?: boolean;
  readonly highlightStyle?: (item: IListItem) => boolean;
  readonly startIndex: number;
  readonly tableRefElement?: HTMLTableElement;
}

export function TableBody<T>(props: IProps<T>) {
  const [stickyLeftLast, setStickyLeftLast] = useState<number | null>(null);
  const [stickyRightFirst, setStickyRightFirst] = useState<number | null>(null);

  useEffect(() => {
    let leftLast: number | null = null;
    let rightFirst: number | null = null;
    props.columns.forEach((it, idx) => {
      if (it.fixed === 'left' && (!leftLast || leftLast < idx)) {
        leftLast = idx;
      }
      if (it.fixed === 'right' && (!rightFirst || rightFirst > idx)) {
        rightFirst = idx;
      }
    });
    setStickyLeftLast(leftLast);
    setStickyRightFirst(rightFirst);

    init();
  }, [props.columns]);

  // 第一列冻结
  const init = () => {};

  const handleResize = () => {
    requestAnimationFrame(() => {
      props.columns.forEach((col, colIndex) => {
        const thElement: any = props.tableRefElement?.getElementsByClassName(
          `row-td-index-${colIndex + 1}`
        );
        if (thElement && col.fixed) {
          const newStyle = getStickyPosition(col, colIndex);
          thElement.forEach((item: any) => {
            Object.assign(item.style, newStyle);
          });
        }
      });
    });
  };

  // 监听 resize 事件
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.columns]);

  const getRowValue = (
    data: T,
    column: ITableColumn<T>,
    columnIndex: number
  ) => {
    const temp = data as IString<any>;
    const value = temp[column.key];
    if (typeof value === 'function') {
      return value(data, column, columnIndex);
    }
    return value;
  };

  const getRowClassName = (dataItem: T, index: number) => {
    if (!props.rowClassName) {
      return null;
    }
    if (typeof props.rowClassName === 'string') {
      return props.rowClassName;
    }
    return props.rowClassName(dataItem, index);
  };

  const getStyle = (isSelected: boolean) => {
    // 选中且没有拖拽的时候，添加move手型
    if (props.isEnableRowDrag && !props.isDragging && isSelected) {
      return {
        cursor: 'move',
      };
    }
    if (props.isDragging && isSelected) {
      return {
        opacity: 0.5,
      };
    }

    return {
      opacity: 1,
    };
  };

  // 处理点击事件
  const onClickRow = (item: T, index: number) => {
    return (e: React.MouseEvent<HTMLDivElement>) => {
      if (!props.onRow) {
        return;
      }
      const event = props.onRow(item, index, props.data);
      if (event.onClick) {
        event.onClick(e);
      }
    };
  };

  const onDragStart = (ev: any, index: number) => {
    if (props?.onDragStart) {
      props.onDragStart(ev, index);
    }
  };

  const onDragEnd = (ev: any) => {
    if (props?.onDragEnd) {
      props.onDragEnd(ev);
    }
  };

  const getStickyPosition = (column: ITableColumn<T>, columnIndex: number) => {
    let currentStyle = {};
    let currentPosition = 0;
    props.columns.forEach((i, idx) => {
      if (column.fixed === 'left') {
        if (idx < columnIndex) {
          const fixedItem = props.tableRefElement?.querySelector(
            `#column-index-${idx + 1}`
          ) as HTMLElement;
          const itemWidth = fixedItem?.offsetWidth || 0;
          currentPosition += itemWidth;
        }
      } else if (column.fixed === 'right') {
        if (idx > columnIndex) {
          const fixedItem = props.tableRefElement?.querySelector(
            `#column-index-${idx + 1}`
          ) as HTMLElement;
          const itemWidth = fixedItem?.offsetWidth || 0;
          currentPosition += itemWidth;
        }
      }
    });
    if (column.fixed === 'left') {
      if (props.rowSelection?.fixed) {
        const selectColumn = props.tableRefElement?.querySelector(
          '.u-table thead > tr > th:nth-child(1)'
        ) as HTMLElement;
        currentPosition += selectColumn?.offsetWidth || 0;
      }
      currentStyle = Object.assign(currentStyle, { left: currentPosition });
    } else if (column.fixed === 'right') {
      currentStyle = Object.assign(currentStyle, { right: currentPosition });
    }
    return currentStyle;
  };

  const renderRowTD = (
    dataItem: T,
    column: ITableColumn<T>,
    columnIndex: number,
    rowIndex: number,
    isTree: boolean,
    id: string, // 树的索引，根据 rowIndex 生成的关系
    isExpandDetail?: boolean,
    expandedText?: string,
    foldText?: string
  ) => {
    if (column.colSpan === 0) {
      return null;
    }
    const text = getRowValue(dataItem, column, columnIndex);
    const className = classNames(
      // getStickyClassName(column.fixed),
      column.fixed ? 'sticky-body-item' : '',
      column.rowTdClassName,
      `row-td-index-${columnIndex + 1}`,
      stickyLeftLast === columnIndex ? 'sticky-left-last' : '',
      stickyRightFirst === columnIndex ? 'sticky-right-first' : ''
    );
    const attrs = getColumnAttrs<T>(dataItem, column, columnIndex, rowIndex);
    if (attrs.rowSpan === 0 || attrs.colSpan === 0) {
      return null;
    }

    // let style = {};
    let style = getStickyPosition(column, columnIndex);
    if (columnIndex === 0) {
      style = { ...style, ...getLevelSpacingStyle(id) };
    }
    let tableCellWidthStyle = {};
    if (props.isEnableResize) {
      tableCellWidthStyle = {
        width: `${props.columnWidths[columnIndex]}px`,
      };
    }

    const renderTdContent = () => {
      const renderWithTooltip = (render: () => ReactNode) => {
        const content = render();
        if (column.showTooltip) {
          return (
            <CustomPopover placement="top" content={content}>
              {content}
            </CustomPopover>
          );
        }
        return content;
      };

      const columnRender = column.render;

      if (columnRender) {
        if (column.isRenderRowPackageLayout === false) {
          return renderWithTooltip(() =>
            columnRender(text, dataItem, columnIndex, rowIndex)
          );
        }
        return renderWithTooltip(() =>
          getRowPackageLayout(
            columnRender(text, dataItem, columnIndex, rowIndex)
          )
        );
      }
      return renderWithTooltip(() => getRowPackageLayout(text));
    };

    const renderContent = () => {
      const row = dataItem as Record<string, any>;
      const rowKeyValue = row[props.rowKey || 'id'] as string;
      if (columnIndex === 0) {
        if (isTree) {
          return (
            <TreeOperatePackage
              onChangeTreeExpand={props.onChangeTreeExpand}
              id={id}
              expandTree={props.expandTree}
              renderIconDown={props.renderIconDown}
            >
              {renderTdContent()}
            </TreeOperatePackage>
          );
        }
        if (isExpandDetail && !props.rowSelection) {
          return (
            <ExpandOperatePackage
              expandedKeys={props.expandedKeys}
              id={rowKeyValue}
              expandedText={expandedText}
              foldText={foldText}
              onChangeExpand={props.onChangeExpand}
            >
              {renderTdContent()}
            </ExpandOperatePackage>
          );
        }
      }
      return renderTdContent();
    };

    return (
      <td
        key={columnIndex}
        className={className}
        {...attrs}
        align={column.align}
        style={style}
      >
        <TableCellRoot style={tableCellWidthStyle} colspan={attrs.colSpan}>
          {renderContent()}
        </TableCellRoot>
      </td>
    );
  };

  const renderTD = (
    dataItem: T,
    rowIndex: number,
    isTree: boolean,
    id: string,
    isExpandDetail?: boolean,
    expandedText?: string,
    foldText?: string
  ) => {
    return props.columns.map((column, columnIndex) => {
      const { collapsed, collapsedIndex } = isColumnCollapse(
        props.columnCollapseIndex,
        columnIndex + 1
      );
      if (collapsed) {
        return addEmptyBody(
          CollapseColumnRenderType.Body,
          collapsedIndex,
          props.data.length
        );
      }
      return renderRowTD(
        dataItem,
        column,
        columnIndex,
        rowIndex,
        isTree,
        id,
        isExpandDetail,
        expandedText,
        foldText
      );
    });
  };

  const renderRowSelection = (
    index: number,
    record: T,
    id: string,
    isExpandDetail?: boolean,
    expandedText?: string,
    foldText?: string
  ) => {
    const { rowSelection } = props;
    if (rowSelection) {
      const rowIndex = index + props.startIndex;
      const row = record as Record<string, any>;
      const rowKeyValue = row[props.rowKey || 'id'] as string;

      const renderSelection = (className = '') => {
        return (
          <RenderRowSelection<T>
            rowSelection={rowSelection}
            rowIndex={rowIndex}
            checked={getBodyCheckedStatus(rowSelection, rowKeyValue)}
            data={props.data}
            fullData={props.fullData}
            record={record}
            rowKey={props.rowKey || 'id'}
            className={className}
          />
        );
      };

      if (isExpandDetail) {
        return (
          <ExpandOperatePackage
            expandedKeys={props.expandedKeys}
            id={rowKeyValue}
            expandedText={expandedText}
            foldText={foldText}
            onChangeExpand={props.onChangeExpand}
          >
            {renderSelection('row-selection-with-expand')}
          </ExpandOperatePackage>
        );
      }

      return renderSelection();
    }
  };

  const renderSummary = () => {
    if (!props.summary?.show) {
      return null;
    }
    return (
      <SummaryComponent<T>
        isRowSelection={!!props.rowSelection}
        columns={props.columns}
        data={props.data}
        summary={props.summary}
        key={-1}
      />
    );
  };

  const renderTrees = (data: ReadonlyArray<T>, id: string): any => {
    return data.map((item, index) => {
      const { isTree, children } = isRowTreeRender(
        item,
        props.rowKey,
        props.tree
      );
      const treeId = `${id}-${index}`;
      if (isTree) {
        const isExpand = isTreeExpand(treeId, props.expandTree);
        return (
          <>
            {renderTR(item, index, isTree, treeId)}
            {isTree && children && isExpand
              ? renderTrees(children, treeId)
              : null}
          </>
        );
      }
      return renderTR(item, index, isTree, treeId);
    });
  };

  const renderTR = (
    item: T,
    index: number,
    isTree: boolean,
    id: string,
    isExpandDetail?: boolean,
    expandedText?: string,
    foldText?: string
  ) => {
    const className = classNames(
      getRowClassName(item, index),
      {
        'tree-mode-row': isTree,
        'expand-mode-row': isExpandDetail,
      },
      props?.highlightStyle && props.highlightStyle(item) ? 'highlight' : '',
      'table-body-tr'
    );

    const row = item as Record<string, any>;
    const rowKeyValue = row[props.rowKey || 'id'];
    const isSelected =
      props.rowSelection?.selectedRowKeys.some((key) => key === rowKeyValue) ||
      false;

    const draggable = !!props.isEnableRowDrag && isSelected;

    return (
      <tr
        key={index}
        className={className}
        onClick={onClickRow(item, index)}
        data-id={id}
        draggable={draggable}
        onDragStart={(ev) => onDragStart(ev, index)}
        onDragEnd={onDragEnd}
        style={getStyle(isSelected)}
      >
        {renderRowSelection(
          index,
          item,
          id,
          isExpandDetail,
          expandedText,
          foldText
        )}
        {renderTD(
          item,
          index,
          isTree,
          id,
          isExpandDetail,
          expandedText,
          foldText
        )}
      </tr>
    );
  };

  const renderRowsTR = (data: ReadonlyArray<T>) => {
    return data.map((item, index) => {
      const { isTree, children } = isRowTreeRender(
        item,
        props.rowKey,
        props.tree
      );
      const id = `${index}`;

      // tree 模式会提示警告，因为当前实现方式的问题，无法设置 key
      if (isTree) {
        const isExpand = isTreeExpand(id, props.expandTree);
        return (
          <>
            {renderTR(item, index, isTree, id)}
            {isTree && children && isExpand ? renderTrees(children, id) : null}
          </>
        );
      }
      if (props.expandDetail) {
        const { expandedText, foldText, className, colSpan } =
          props.expandDetail;
        const row = item as Record<string, any>;
        const rowKeyValue = row[props.rowKey || 'id'];
        const isExpand = isExpandDetail(rowKeyValue, props.expandedKeys);
        return (
          <>
            {renderTR(item, index, isTree, id, true, expandedText, foldText)}
            {isExpand ? (
              <tr className="table-expand-tr">
                <td
                  colSpan={colSpan}
                  className={classNames('table-expand-td', className)}
                >
                  {props.expandDetail.render(item)}
                </td>
              </tr>
            ) : null}
          </>
        );
      }
      return renderTR(item, index, isTree, id);
    });
  };

  removeIndexCache(CollapseColumnRenderType.Body);

  const renderTableBody = () => {
    // 合计展示再表头还是表尾
    if (props?.summary?.position === 'end') {
      return (
        <>
          {renderSummary()}
          {renderRowsTR(props.data)}
        </>
      );
    }
    return (
      <>
        {renderRowsTR(props.data)}
        {renderSummary()}
      </>
    );
  };
  return renderTableBody();
}
